import { vaultItemIdSchema } from "@estate-platform/domain/ids";
import { createFileRoute, getRouteApi, useLocation } from "@tanstack/react-router";

import { useViewer } from "../data/hooks.ts";
import { parameterOrNotFound } from "../shell/route-parameters.ts";
import { vaultItemSearch } from "../shell/search.ts";
import { NoAccessPage, NotFoundPage } from "../shell/status-pages.tsx";
import { loadVaultItem } from "../vault/loaders.ts";
import { VaultItemPending } from "../vault/pending.tsx";
import { canAddVaultItems } from "../vault/permissions.ts";
import { VaultItemPage } from "../vault/vault-item-page.tsx";

import type { Viewer } from "../data/viewer.ts";
import type { Arrival } from "../vault/arrival.ts";
import type { ReactElement } from "react";

const route = getRouteApi("/_app/vault/$id");

const arrivalOf = (isRevealAsked: boolean, isRevealLinked: boolean): Arrival => {
  if (isRevealAsked) {
    return "reveal";
  }
  return isRevealLinked ? "focus" : null;
};

const ItemPage = ({ viewer }: { readonly viewer: Viewer }): ReactElement => {
  const { id } = route.useParams();
  const isRevealAsked = useLocation({ select: (location) => location.state.revealOnArrival === true });
  const isRevealLinked = route.useSearch({ select: (search) => search.reveal === 1 });
  return <VaultItemPage viewer={viewer} itemId={id} arrival={arrivalOf(isRevealAsked, isRevealLinked)} />;
};

const Page = (): ReactElement => {
  const viewer = useViewer();
  if (viewer === null) {
    return <NoAccessPage />;
  }
  return canAddVaultItems(viewer) ? <ItemPage viewer={viewer} /> : <NotFoundPage />;
};

export const Route = createFileRoute("/_app/vault/$id")({
  params: {
    parse: ({ id }) => ({ id: parameterOrNotFound(vaultItemIdSchema, id) }),
    stringify: ({ id }) => ({ id }),
  },
  validateSearch: vaultItemSearch,
  loader: async ({ context, params }) => loadVaultItem(context.data, params.id),
  pendingComponent: VaultItemPending,
  component: Page,
});
